<script setup lang="ts">
import { onMounted, computed, ref, watch, nextTick } from 'vue';
import { BaseButton, BaseCircleButton, BaseIcon, BaseOverlay } from '@/components/Base';
import { useVideo } from '@/composables/useVideo';
import { useDeviceOrientation } from '@/composables/useDeviceOrientation';
import VideoState from './VideoState.vue';
import CameraPermissionTutorial from './CameraPermissionTutorial.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Landscape orientation detection
const { isLandscape } = useDeviceOrientation();

// Refs for DOM elements
const video = ref<HTMLVideoElement | null>(null);

const {
  switchFacingMode,
  videoState,
  cameraStream,
  startCamera,
  stopCamera,
  cameraWasEverGranted
} = useVideo();

const cameraEnabled = computed(() => cameraStream.value != null);

async function applyStreamToVideoEl() {
  if (!video.value) {
    return;
  }

  try {
    video.value.srcObject = cameraStream.value;
    await video.value.play();
  } catch (e) {
    console.warn("Failed to apply stream to video element", e);
  }
}

watch(cameraStream, async () => {
  console.log("Camera stream changed in NgVideo.vue:", cameraStream.value);
  if (cameraStream.value == null) {
    // TODO ošetřit tenhle stav a nastylovat dle něj video obrazovku
    // return;
  }

  await nextTick(); // Wait for DOM to update (v-if toggle)
  await applyStreamToVideoEl();
}, { deep: true, immediate: true });

onMounted(async () => {
  // if stream already set before component mounted, ensure it's applied
  await applyStreamToVideoEl();
});

async function toggleCameraEnabled() {
  if (cameraEnabled.value) {
    await stopCamera();
  } else {
    await startCamera();
  }
}
</script>

<template>
  <div class="video-view">
    <!-- Landscape orientation warning overlay -->
    <BaseOverlay v-if="isLandscape">
      <BaseIcon name="rotate-phone" :size="64" color="white" />
      <p class="landscape-overlay__text">{{ t('video.rotateDevice') }}</p>
    </BaseOverlay>

    <!-- Top Navigation -->
    <nav class="actions-wrapper top-actions">
      <div></div>
      <VideoState :state="videoState" style="place-self: center;" />
      <BaseCircleButton style="place-self: flex-end;" icon="camera-switch" :size="60" :icon-size="36" @click="switchFacingMode" />
    </nav>

    <!-- Video Container -->
    <div class="video-container" v-if="cameraStream">
      <video ref="video" id="localVideo" autoplay muted playsinline></video>
      <canvas ref="canvas" id="canvas"></canvas>
    </div>

    <div class="video-container no-camera" v-else>
      <div v-if="cameraWasEverGranted" class="camera-paused">
        <BaseIcon name="end-video" :size="64" />
        <p class="camera-paused__text">{{ t('video.cameraPaused') }}</p>
      </div>
      <CameraPermissionTutorial v-else />
    </div>

    <!-- Control buttons -->
    <div class="actions-wrapper bottom-actions">
      <BaseButton
        :left-icon="cameraEnabled ? 'end-video' : 'start-video'"
        :variant="cameraEnabled ? 'danger' : 'success'"
        :hold-to-confirm="cameraEnabled"
        size="medium"
        @click="toggleCameraEnabled"
      >
        <p v-if="cameraEnabled">{{ t('video.turnOffCamera') }}</p>
        <p v-else>{{ t('video.turnOnCamera') }}</p>
      </BaseButton>
    </div>
  </div>
</template>

<style scoped>
.actions-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 10;
  padding: var(--spacing-md);
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  align-items: center;
}

.top-actions {
  top: 0;
}

.bottom-actions {
  justify-content: center;
  bottom: var(--spacing-xl, 24px);
  grid-template-columns: repeat(1, auto);
  padding: var(--spacing-lg) var(--spacing-md);
}

.video-view {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--color-video-background);
}

.video-container {
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-camera {
  padding: var(--spacing-md);
  text-align: center;
}

#localVideo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

#canvas {
  display: none;
}

.landscape-overlay__text {
  color: var(--color-text-light);
  font-size: var(--font-size-lg);
  max-width: 300px;
  line-height: 1.4;
}

.camera-paused {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--color-text-light, #fff);
}

.camera-paused__text {
  font-size: var(--font-size-lg);
  font-weight: 600;
  max-width: 280px;
  line-height: 1.4;
}
</style>
